import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCounsellingNoteForPatientByTask } from '../../../global/entities/ClinicalNote/queries/useCounsellingNoteForPatientByTask';
import { useAcknowledgeCounsellingNote } from '../../../global/entities/ClinicalNote/mutations';
import { useSessionProvider } from '../../../global/provider/sessionContext';

const CounsellingAcknowledgementPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: taskId } = useParams<{ id: string }>();
  const { currentUser } = useSessionProvider();

  // The patient often arrives here from an email link, where i18next may have
  // cached an English locale before the session cookie/preferred language was
  // applied. Sync to the patient's stored language as soon as we know it.
  useEffect(() => {
    const lang = currentUser?.language;
    if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
  }, [currentUser?.language, i18n]);

  const { data: note, isLoading } = useCounsellingNoteForPatientByTask(taskId);
  const acknowledge = useAcknowledgeCounsellingNote();

  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState('');

  if (isLoading) {
    return (
      <Paper p="lg" withBorder>
        <Loader size="sm" />
      </Paper>
    );
  }

  if (!note) {
    return (
      <Paper p="lg" withBorder>
        <Text>{t('global.notFound', 'Not found')}</Text>
      </Paper>
    );
  }

  const formatDate = (d: string | Date | null) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
    });
  };

  const formatDateTime = (d: string | Date | null) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const handleSubmit = async () => {
    if (!confirmed) return;
    try {
      await acknowledge.mutateAsync({
        noteId: String(note.id),
        notes: notes.trim() || null,
      });
      navigate('/patientDashboard');
    } catch {
      // error path stays on page; mutation surface no toaster.
    }
  };

  const alreadyAcknowledged = note.patientAcknowledged;

  return (
    <Paper p="lg" shadow="xs" withBorder>
      <Stack gap="md">
        <Title order={3}>{t('counsellingNote.patient.pageTitle')}</Title>

        {note.hcpSigned ? (
          <Text size="sm" c="dimmed">
            {t('counsellingNote.patient.hcpSignature', {
              hcpName: note.hcpSignedByName ?? '',
              date: formatDate(note.hcpSignedAtUtc ?? null),
            })}
          </Text>
        ) : (
          <Alert color="yellow">
            {t('counsellingNote.patient.hcpNotSigned')}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Title order={5} mb="xs">
            {t('counsellingNote.patient.noteHeader')} —{' '}
            {formatDateTime(note.examDate)}
          </Title>
          {note.freeTextNote && (
            <Text style={{ whiteSpace: 'pre-wrap' }}>{note.freeTextNote}</Text>
          )}
        </Paper>

        {alreadyAcknowledged ? (
          <Alert
            color="green"
            title={t('counsellingNote.patient.alreadyAcknowledgedTitle')}
          >
            {t('counsellingNote.patient.alreadyAcknowledged', {
              date: formatDate(note.patientAcknowledgedAtUtc ?? null),
            })}
          </Alert>
        ) : (
          <>
            <Checkbox
              label={t('counsellingNote.patient.acknowledgeCheckbox')}
              checked={confirmed}
              onChange={(event) => setConfirmed(event.currentTarget.checked)}
            />

            <Textarea
              label={t('counsellingNote.patient.notesLabel')}
              placeholder={t('counsellingNote.patient.notesPlaceholder') ?? ''}
              minRows={3}
              value={notes}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />

            {acknowledge.isError && (
              <Alert
                color="red"
                title={t('counsellingNote.patient.errorTitle')}
              >
                {t('counsellingNote.patient.errorMessage')}
              </Alert>
            )}

            <Button
              disabled={!confirmed || acknowledge.isPending}
              loading={acknowledge.isPending}
              onClick={handleSubmit}
            >
              {t('counsellingNote.patient.submit')}
            </Button>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default CounsellingAcknowledgementPage;
